import { ICompileService } from "angular";
import { RecordDataSource } from "../../../records/grids";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { moneyValueGetterFactory } from "../../../records/grids/cells/valueGetters";
import { ColDef } from "../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { StateService } from "@uirouter/angularjs";
import { startCase } from "lodash";
import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import { machineConfigurationObjectName } from "../../../machines/shared/names";

const gridId = Symbol("main.depreciation.grid");
export class DepreciationViewController {
  static $inject = [
    ServerUtilityName,
    GridSettingsServiceName,
    ColumnDefinitionServiceName,
    "$compile",
    "$state",
    ColumnDefinitionSetServiceName,
  ];

  readonly gridDataSource: RecordDataSource;
  readonly gridSettings: IGridSettings;
  readonly columns: ColDef[];
  readonly defaultQuery: IQuery = {};

  constructor(
    serverUtility: ServerUtility,
    gridSettingsService: GridSettingsService,
    columnDefinitionService: ColumnDefinitionService,
    $compile: ICompileService,
    private readonly $state: StateService,
    columnDefinitionSetService: ColumnDefinitionSetService
  ) {
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      "api/machines/depreciation"
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.columns = [
      columnDefinitionService.getLinkColDef({
        headerName: "info",
        iconClass: "fa fa-info-circle text-primary",
        colId: "detail",
        width: 20,
        stateExpression: "main.depreciation.info({ machineId: data.MachineId})",
      }),
      columnDefinitionService.getDateColDef({
        field: "UsefulLifeStartDate",
        header: "Useful Life Start Date ",
      }),
      columnDefinitionService.getDateColDef({
        field: "UsefulLifeEndDate",
        header: "Useful Life End Date ",
      }),
      columnDefinitionService.getNumberColDef({
        field: "UsefulLife",
        header: "Useful Life",
      }),
      columnDefinitionService.getNumberColDef({
        field: "PurchaseCost",
        header: "Purchase Cost",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "SalvageValue",
        header: "Salvage Value",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getStringColDef({
        field: "DepreciationMethod",
        header: "Depreciation Method",
        valueGetter: (params) =>
          startCase((<any>params.data).DepreciationMethod),
        filter: new FilterProvider("DepreciationMethod", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Straight Line", value: "StraightLine" },
                {
                  display: "Double Declining Balance",
                  value: "DoubleDecliningBalance",
                },
                {
                  display: "Sum Of The Years Digits",
                  value: "SumOfTheYearsDigits",
                },
              ],
              queryFieldNames: ["DepreciationMethod"],
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineDepreciationDetailYearStartValue",
        header: "Book Value Year Start",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineDepreciationDetailDepreciatedValue",
        header: "Book Value Year End",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineDepreciationDetailAccumulatedDepreciation",
        header: "Accumulated Depreciation",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineDepreciationDetailDepreciationExpense",
        header: "Depreciation Expense",
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getDateColDef({
        field: "MachineDepreciationDetailStartDate",
        header: "Start Date",
      }),
      columnDefinitionService.getDateColDef({
        field: "MachineDepreciationDetailEndDate",
        header: "End Date",
      }),
      ...columnDefinitionSetService.getColumnDefinitionSet(
        machineConfigurationObjectName
      ),
      columnDefinitionService.getLinkColDef({
        headerName: "edit",
        iconClass: "fa fa-pencil text-primary",
        colId: "edit",
        stateExpression: "main.depreciation.Edit({ machineId: data.MachineId})",
      }),
    ];
  }

  async add() {
    await this.$state.go("^.Add", undefined, { reload: true });
  }
}
